import Image from 'next/image';

import { NEW_TAB_HINT, REVIEW_SOURCE_LINK_SUFFIX } from '@/content/a11y-ui';
import { REVIEW_SOURCE_LABELS } from '@/content/testimonial-feed';
import type { ReviewSource } from '@/sanity/types';

/**
 * Square, badge-sized marks. Deliberately NOT the Sanity-managed
 * `siteSettings.brandBadges` logos nor the full-size files beside them:
 *
 *  - clutch-logo.png is 1200×630 with the mark filling only the middle third,
 *    so it vanishes to a sliver at 16px. clutch-logo.svg is a square 40×40.
 *  - upwork-logo.png is a 2498×2500, 223 KB original. upwork-mark-48.png is
 *    the same artwork at 48×48 / 2.3 KB — these render `unoptimized` (see
 *    below), so the file that ships is the file on disk.
 */
const SOURCE_LOGOS: Record<ReviewSource, string> = {
  clutch: '/images/badges/clutch-logo.svg',
  upwork: '/images/badges/upwork-mark-48.png',
};

interface VerifiedReviewBadgeProps {
  source: ReviewSource;
  /** Extra classes for the wrapper — used to tune spacing per carousel. */
  className?: string;
  /**
   * Deep link to this specific review. When present the chip becomes a link,
   * so the quote can be checked at the source rather than taken on trust.
   * Callers pass it only for a review the visitor can actually reach — see
   * the note on tab stops below.
   */
  href?: string;
  /**
   * Accessible name for the link. Defaults to "Read this review on <platform>",
   * which is only true for a deep link to one review — a caller linking to the
   * platform *profile* instead passes an honest label here.
   */
  linkLabel?: string;
  /** `dark` for a chip sitting on the dark (#121212) surface. */
  tone?: 'light' | 'dark';
}

/**
 * "Verified on Clutch" / "Verified on Upwork" chip shown under a testimonial's
 * reviewer name, marking which public profile the review was published on.
 *
 * Plain text by default, for the reason it was written that way: it sits in a
 * carousel slide next to the prev/next controls, and a tab stop in every slide
 * — including the ones nobody can see — would clutter keyboard navigation. The
 * section-level trust badges already link out to the profiles.
 *
 * With `href` it becomes a link to *that* review, which is a different thing
 * from a profile link: it is what lets a visitor check one quote (finding
 * conversion-zpvepn). The tab-stop objection is answered by the caller rather
 * than by refusing the link — `ServiceTestimonials` passes `href` only for the
 * active slide, and `Testimonials` renders one review at a time — so a
 * carousel gains at most one tab stop, never one per slide. That matters
 * beyond tidiness: inactive slides carry `aria-hidden`, and a focusable node
 * inside an `aria-hidden` subtree is an ARIA violation.
 *
 * Both carousels render on a light surface, so the colours are tuned for dark
 * text on light; the wrapper tint is alpha-based to survive the shared-gradient
 * backdrop that `Testimonials` switches to via `onDarkBackdrop`.
 */
export function VerifiedReviewBadge({
  source,
  className = '',
  href,
  linkLabel,
  tone = 'light',
}: VerifiedReviewBadgeProps): React.JSX.Element {
  const dark = tone === 'dark';
  const Tag = href ? 'a' : 'span';
  const linkProps = href
    ? ({
        href,
        target: '_blank',
        rel: 'noopener noreferrer nofollow',
        // Label-in-name (WCAG 2.5.3): the accessible name must START with the
        // visible chip text, then add where the link goes and the new-tab hint.
        'aria-label': `${REVIEW_SOURCE_LABELS[source]}: ${linkLabel ?? REVIEW_SOURCE_LINK_SUFFIX} ${NEW_TAB_HINT}`,
        'data-analytics-cta': 'review_source_link',
      } as const)
    : {};

  return (
    <Tag
      {...linkProps}
      className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2 py-1 lg:gap-2 lg:px-2.5 ${dark ? 'bg-white/10' : 'bg-[#121212]/[0.06]'} ${href ? `underline-offset-4 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${dark ? 'hover:bg-white/[0.16]' : 'hover:bg-[#121212]/[0.12]'}` : ''} ${className}`}
    >
      {/* `unoptimized`: at 16px there is nothing for an optimizer to save, and
          next.config.ts sets `loader: 'custom'`, which takes the built-in
          /_next/image endpoint out of service — every local /public asset 404s
          through it. Serving these two marks straight from /public sidesteps
          that entirely. */}
      <Image
        src={SOURCE_LOGOS[source]}
        alt=""
        width={40}
        height={40}
        unoptimized
        aria-hidden="true"
        className="size-3.5 shrink-0 rounded-[3px] object-contain lg:size-4"
      />
      <span
        className={`text-[11px] font-normal leading-[1.2] tracking-[-0.01em] lg:text-[13px] ${dark ? 'text-white/80' : 'text-[#121212]/70'}`}
      >
        {REVIEW_SOURCE_LABELS[source]}
      </span>
    </Tag>
  );
}
